import React, {useEffect, useRef, useState} from "react";
import {gsap} from "gsap";
import "./ListGrid.css";
import {Link} from "react-router-dom";

const ListGrid = ({projects}) => {
  const previewRef = useRef(null);
  const xToRef = useRef(null);
  const yToRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeImage, setActiveImage] = useState("");

  // Mouse follow behavior
  const followMouse = (e) => {
    if (xToRef.current && yToRef.current) {
      xToRef.current(e.pageX);
      yToRef.current(e.pageY);
    }
  };

  const enableMouseFollow = () => {
    if (!previewRef.current) return;

    gsap.set(previewRef.current, {
      xPercent: -50,
      yPercent: -50,
      display: "block",
    });

    xToRef.current = gsap.quickTo(previewRef.current, "x", {
      duration: 0.4,
      ease: "power2.out",
    });
    yToRef.current = gsap.quickTo(previewRef.current, "y", {
      duration: 0.4,
      ease: "power2.out",
    });

    window.addEventListener("mousemove", followMouse);
  };

  const disableMouseFollow = () => {
    window.removeEventListener("mousemove", followMouse);
    if (previewRef.current) {
      gsap.set(previewRef.current, {clearProps: "all", display: "none"});
    }
  };

  const handleMouseFollow = () => {
    const isNowMobile = window.innerWidth < 768;
    setIsMobile(isNowMobile);

    if (isNowMobile) {
      disableMouseFollow();
    } else {
      enableMouseFollow();
    }
  };

  useEffect(() => {
    handleMouseFollow();
    window.addEventListener("resize", handleMouseFollow);
    return () => {
      disableMouseFollow();
      window.removeEventListener("resize", handleMouseFollow);
    };
  }, []);

  return (
    <section className="section">
      <div
        className={`portfolio-list-section ${
          isMobile ? "is-mobile" : "project-list-item"
        }`}
      >
        <div className="project-preview-list project-list-portrait project-list-hover">
          <div className="project-list-items-list">
            {/* Floating Preview image (inside item list) for Desktop only */}
            {!isMobile && (
              <div className="project-list-preview" ref={previewRef}>
                <div className="project-list-preview-image">
                  {activeImage && (
                    <img
                      key={activeImage}
                      src={activeImage}
                      alt="Preview"
                      style={{
                        opacity: 1,
                        transition: "opacity 0.4s ease-in-out",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {projects.map((project, index) => (
              <a
                href={`/projects/${project.slug}`}
                key={index}
                className="project-list-item"
                onMouseEnter={() => {
                  if (!isMobile) {
                    setActiveImage(project.image);
                    gsap.to(previewRef.current, {autoAlpha: 1, duration: 0.3});
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobile) {
                    setActiveImage("");
                    gsap.to(previewRef.current, {autoAlpha: 0, duration: 0.3});
                  }
                }}
              >
                {/* Mobile inline image preview */}
                {isMobile && (
                  <div className="project-list-preview">
                    <div className="project-list-preview-image">
                      <img
                        src={project.image}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="project-list-item-inner">
                  <div className="project-list-item-holder">
                    <div className="project-list-col project-list-col-count">
                      <div className="project-list-count"></div>
                    </div>

                    <div className="project-list-col project-list-col-caption">
                      <div className="project-list-caption">
                        <h2 className="project-list-title">{project.name}</h2>
                        <div className="project-list-categories">
                          <div className="project-list-category">
                            {project.tags.map((tag, idx) => (
                              <span key={idx} className={tag.color}>
                                #{tag.name}
                                {idx < project.tags.length - 1 ? ", " : ""}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="project-list-col project-list-col-info project-list-justify-content-md-end">
                      <div className="project-list-info">
                        {project.category}
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ListGrid;
